import { HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { filter, take } from 'rxjs';
import { ErrorZone } from '../enums/error-zone';
import { PrimeNGToastSeverity } from '../enums/primeng-toast-severity';
import { isGloballyHandledError } from '../utils/http-error';
import { ConstantsService } from './constants.service';
import { LocaleService } from './locale.service';

@Injectable({
    providedIn: 'root',
})
export class ErrorService {
    private readonly destroyRef = inject(DestroyRef);
    private readonly localeService = inject(LocaleService);
    private readonly toastService = inject(MessageService);
    private readonly translateService = inject(TranslateService);

    /**
     * Configures the display of an error. Content configured by parameters.
     *
     * @param summary toast summary text
     * @param detail toast detail text
     */
    public errorToast(summary: string, detail: string): void {
        this.whenLanguageSelected(() => {
            this.toastService.add({
                key: ConstantsService.ADMIN_ERROR_TOAST_KEY,
                severity: PrimeNGToastSeverity.ERROR,
                summary: this.translateService.instant(summary),
                detail: this.translateService.instant(detail),
            });
        });
    }

    /**
     * Configures the display of an error, ignoring globally handled errors.
     *
     * @param summary toast summary text
     * @param detail toast detail text
     * @param error HTTP error response
     */
    public errorToastIgnoringGloballyHandledErrors(
        summary: string,
        detail: string,
        error: HttpErrorResponse
    ): void {
        if (isGloballyHandledError(error)) {
            return;
        }

        this.errorToast(summary, detail);
    }

    /**
     * Configures the display of a generic error. Content configured by zone where the error is displayed.
     *
     * @param zone zone in which the toast is displayed
     */
    public generalToast(zone: ErrorZone): void {
        this.whenLanguageSelected(() => {
            this.toastService.add({
                key: ConstantsService.ADMIN_ERROR_TOAST_KEY,
                severity: PrimeNGToastSeverity.ERROR,
                summary: this.translateService.instant(
                    `shared.error.${zone}.summary`
                ),
                detail: this.translateService.instant(
                    `shared.error.${zone}.detail`
                ),
            });
        });
    }

    /**
     * Configures the display of a generic error, ignoring globally handled errors.
     *
     * @param zone zone in which the toast is displayed
     * @param error HTTP error response
     */
    public generalToastIgnoringGloballyHandledErrors(
        zone: ErrorZone,
        error: HttpErrorResponse
    ): void {
        if (isGloballyHandledError(error)) {
            return;
        }

        this.generalToast(zone);
    }

    /**
     * Defers `action` until the app's language (including PrimeNG's) has resolved - e.g. a
     * route guard can redirect and raise a toast before the first navigation completes. Once
     * resolved, `languageSelected$` stays `true`, so this runs synchronously for every other
     * caller.
     */
    private whenLanguageSelected(action: () => void): void {
        this.localeService.languageSelected$
            .pipe(
                filter((selected) => selected),
                take(1),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(action);
    }
}
